import React from 'react';
import styled from 'styled-components';
import { Button, Dropdown, Tabs, Input, Popover } from 'freemium-ui';
import { useController, useFormContext } from 'react-hook-form';

import Icons from 'components/icons';
import Spacer from 'components/Spacer';
import { useOnClickOutside } from 'utils/hooks';
import {
  StyledButtonMenu,
  StyledScaleCustomize,
  StyledButtonCustomize,
} from './style';
import HFDropdown from 'components/hook-form-fields/Dropdown';
import NPSSlider from 'components/NPSSlider';
import BlockItemComponent from '../block-item-component';
import { omit } from 'lodash';

export type CustomizeProps = {
  values: any;
  setValues: Function;
  block: any;
  blocks: any;
  surveyForm: any;
};

const options = [
  {
    id: 'scale',
    name: 'Scale',
  },
  {
    id: 'logical',
    name: 'Skip Logic',
  },
];

const allOptions = [
  { id: 'next', label: 'Next question' },
  { id: 'specific', label: 'Specific question' },
  { id: 'logic', label: 'Based on answer' },
  { id: 'end', label: 'End the survey' },
];

const PRESETS = [
  {
    id: 'default',
    start: 'Very Low',
    end: 'Very High',
  },
  {
    id: '2',
    start: 'Strongly disagree',
    end: 'Strongly agree',
  },
  {
    id: '3',
    start: 'Very dissatisfied',
    end: 'Very satisfied',
  },
  {
    id: '4',
    start: 'Very unlikely',
    end: 'Very likely',
  },
  {
    id: '5',
    start: 'Strongly disapprove',
    end: 'Strongly approve',
  },
];

const SLIDER_OPTIONS = [
  { min: 0, max: 6, id: 'detractor' },
  { min: 7, max: 8, id: 'passive' },
  { min: 9, max: 10, id: 'promoter' },
];

export const Customize = ({
  values,
  blocks,
  setValues,
  block,
}: // surveyForm,
CustomizeProps) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { register, watch, setValue, control, getValues } = useFormContext();

  const surveyForm = getValues();

  const {
    type_info: {
      linear_scale: {
        button_style = 'highlighted',
        button_shape = 'rounded',
      } = {},
      rating_presets: { start = 'Very unlikely', end = 'Very likely' } = {},
      validation: { min, max },
      meta = {
        branches: SLIDER_OPTIONS,
      },
    },
  } = values;

  const [value, setTabValue] = React.useState(options[0].id);

  const setFieldValues = (key: string, value: any) => {
    setValues({
      ...values,
      type_info: {
        ...values.type_info,
        [key]: value,
      },
    });
  };

  const nextBlocks = blocks.slice(
    blocks.findIndex(
      (block: any) => block.question_names.indexOf(values.name) > -1,
    ) + 1,
  );

  const question = surveyForm.question_details?.questions.find(
    (question: any) => question.name === block?.question_names[0],
  );

  const questionIndex = surveyForm.question_details?.questions.findIndex(
    (q: any) => q.name === question?.name,
  );

  const watchBranching =
    watch(
      `question_details.questions[${questionIndex}].type_info.meta.block.branchOption`,
    ) || allOptions[0].id;

  const isActive = surveyForm.state === 'ACTIVE';
  return (
    <div>
      <StyledButtonCustomize>
        <Button
          type="secondary"
          inline
          aria-describedby="basic-popover"
          onClick={(e: any) => setAnchorEl(e.currentTarget)}
          style={{
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            width: 'auto',
            padding: '2px 5px',
            maxHeight: '27px',
          }}
        >
          <Icons glyph="customize" size="21" viewBox="-2 -3 25 25" />
        </Button>
      </StyledButtonCustomize>
      <Popover
        id="basic-popover"
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <StyledScaleCustomize onClick={(e: any) => e.stopPropagation()}>
          <Tabs
            value={value}
            onChange={(e: any, v: any) => {
              e.preventDefault();
              e.stopPropagation();
              setTabValue(v.id);
            }}
            options={options}
          />
          {value === options[0].id && (
            <div className="content">
              <div className="field inline">
                <label htmlFor="scale">Scale starts from</label>
                <ScaleStartField
                  question={
                    surveyForm.question_details.questions[questionIndex]
                  }
                  defaultValue={
                    surveyForm.question_details.questions[questionIndex]
                      .type_info.validation.min
                  }
                  control={control}
                  questionIndex={questionIndex}
                  disabled={isActive}
                />
              </div>
              <div className="field inline">
                <div className="half">
                  <label className="is-required" htmlFor="lowRating">
                    Low rating
                  </label>
                  <Spacer y={0.2} />
                  <Input
                    {...register(
                      `question_details.questions[${questionIndex}].type_info.rating_presets.start`,
                    )}
                    defaultValue={
                      surveyForm.question_details.questions[questionIndex]
                        .type_info.rating_presets.start
                    }
                    inputStyle={{
                      minWidth: '110px',
                    }}
                    disabled={isActive}
                    maxLength={25}
                  />
                </div>
                <div className="half">
                  <label className="is-required" htmlFor="highRating">
                    High rating
                  </label>
                  <Spacer y={0.2} />
                  <Input
                    inputStyle={{
                      minWidth: '110px',
                    }}
                    {...register(
                      `question_details.questions[${questionIndex}].type_info.rating_presets.end`,
                    )}
                    defaultValue={
                      surveyForm.question_details.questions[questionIndex]
                        .type_info.rating_presets.end
                    }
                    disabled={isActive}
                    maxLength={25}
                  />
                </div>
                <div className="half"></div>
                <PresetsList
                  onChange={(change: any) => {
                    setFieldValues('rating_presets', change);
                  }}
                  disabled={isActive}
                />
              </div>
              <div className="field ">
                <label htmlFor="style" className="is-required">
                  Button style
                </label>
                <Spacer y={0.1} />
                <ButtonMenu
                  active={button_style}
                  options={[
                    { id: 'highlighted', label: 'Highlighted' },
                    { id: 'standard', label: 'Standard' },
                  ]}
                  name={`question_details.questions[${questionIndex}].type_info.linear_scale.button_style`}
                  control={control}
                  disabled={isActive}
                />
              </div>
              <div className="field ">
                <label htmlFor="shape" className="is-required">
                  Button shape
                </label>
                <Spacer y={0.1} />
                <ButtonMenu
                  hideLabel={true}
                  name={`question_details.questions[${questionIndex}].type_info.linear_scale.button_shape`}
                  control={control}
                  active={button_shape}
                  options={[
                    { id: 'rounded', label: 'Rounded' },
                    { id: 'curved', label: 'Curved' },
                    { id: 'square', label: 'Boxed' },
                  ]}
                  disabled={isActive}
                />
              </div>
            </div>
          )}

          {value === options[1].id && (
            <div className="content">
              <div className="field">
                <label>Skip Logic</label>
                <Spacer y={0.3} />
                <Dropdown
                  defaultSelectedItem={allOptions.find(
                    (opt: any) => opt.id === watchBranching,
                  )}
                  items={allOptions}
                  itemToString={(item: any) => item.label}
                  onChange={(item: any) => {
                    const meta = watch(
                      `question_details.questions[${questionIndex}].type_info.meta`,
                    );
                    const updatedMeta =
                      item.id !== 'logic'
                        ? {
                            ...omit(meta, ['branches']),
                          }
                        : meta;
                    setValue(
                      `question_details.questions[${questionIndex}].type_info.meta`,
                      {
                        ...updatedMeta,
                        block: {
                          ...updatedMeta.block,
                          branchOption: item.id,
                        },
                      },
                    );
                  }}
                  placeholder="-- --- --"
                  disabled={isActive}
                />
              </div>
              {watchBranching === allOptions[1].id && (
                <div className="field">
                  <label>Question</label>
                  <Spacer y={0.3} />
                  <HFDropdown
                    itemKeyId="name"
                    defaultValue={meta?.block?.nextBlock}
                    items={nextBlocks.filter(
                      (block: any) => block.name !== 'TY',
                    )}
                    name={`question_details.questions[${questionIndex}].type_info.meta.block.nextBlock`}
                    itemToString={(item: any) => item.title}
                    placeholder="Add follow up questions"
                    disabled={isActive}
                    itemComponent={(currentBlock: any) => (
                      <BlockItemComponent
                        currentBlock={currentBlock}
                        surveyForm={surveyForm}
                        blocks={blocks}
                      />
                    )}
                  />
                </div>
              )}
              {watchBranching === allOptions[2].id && (
                <>
                  {SLIDER_OPTIONS.map((option: any, index: number) => (
                    <div className="field" key={`sl-${option.id}-${index}`}>
                      <label className="block required" htmlFor="start">
                        If the answer is{' '}
                        <NPSSlider
                          range={
                            option.id === 'detractor'
                              ? [min, 6]
                              : option.id === 'passive'
                              ? [7, 8]
                              : [9, 10]
                          }
                        />{' '}
                        [{option.id}], go to
                      </label>
                      <Spacer y={0.3} />
                      <div className="follow-up">
                        <LogicalJumpDropdown
                          questionIndex={questionIndex}
                          nextBlocks={nextBlocks}
                          control={control}
                          option={option}
                          disabled={isActive}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </StyledScaleCustomize>
      </Popover>
    </div>
  );
};

export default Customize;

const LogicalJumpDropdown = ({
  questionIndex,
  nextBlocks,
  control,
  option,
  disabled,
}: any) => {
  const { field } = useController({
    name: `question_details.questions[${questionIndex}].type_info.meta.branches`,
    control,
    defaultValue: SLIDER_OPTIONS,
  });

  return (
    <>
      <Dropdown
        defaultSelectedItem={
          field.value.find((b: any) => b.id === option.id).nextBlock &&
          nextBlocks.find(
            (block: any) =>
              block.name ===
              field.value.find((b: any) => b.id === option.id).nextBlock,
          )
            ? nextBlocks.find(
                (nb: any) =>
                  field.value.find((b: any) => b.id === option.id).nextBlock ===
                  nb.name,
              )
            : nextBlocks[0]
        }
        items={nextBlocks}
        onChange={(item: any) => {
          field.onChange(
            field.value?.map((b: any) =>
              b.id === option.id ? { ...b, nextBlock: item.name } : b,
            ),
          );
        }}
        itemToString={(item: any) =>
          item.title === 'Thank you' ? 'End Survey' : item.title
        }
        disabled={disabled}
        placeholder="Add follow up questions"
      />
    </>
  );
};

const ButtonMenu = ({
  options,
  active,
  name,
  control,
  hideLabel,
  disabled,
}: {
  options: any[];
  hideLabel?: boolean;
  active: any;
  name: any;
  control: any;
  disabled: boolean;
}) => {
  const { field } = useController({
    name,
    control,
    defaultValue: active,
  });

  return (
    <StyledButtonMenu>
      <div className={`button-menu tabs ${hideLabel ? 'scale' : ''}`}>
        {options.map((option: any) => (
          <div
            key={option.id}
            className={`${option.id === field.value ? 'active' : ''} ${
              option.id
            } button-value`}
            onClick={(e: any) => {
              e.stopPropagation();
              !disabled && field.onChange(option.id);
            }}
          >
            <span className="btn-style">{hideLabel && 8}</span>
            <span className="btn-style active">{hideLabel && 9}</span>
            <span className="btn-style">{hideLabel && 10}</span>
            {!hideLabel && <span className="label">{option.label}</span>}
          </div>
        ))}
      </div>
    </StyledButtonMenu>
  );
};

const PresetsList = ({
  onChange,
  disabled,
}: {
  onChange: any;
  disabled: boolean;
}) => {
  const componentRef = React.useRef<HTMLDivElement>(null);

  const [pickerOpen, setPickerOpen] = React.useState<boolean>(false);

  useOnClickOutside(componentRef, () => {
    setPickerOpen(false);
  });

  return (
    <div className="half push-right" ref={componentRef}>
      <div className="presets">
        <a
          onClick={(e: any) => {
            e.preventDefault();
            !disabled && setPickerOpen(!pickerOpen);
          }}
        >
          Use presets
        </a>
      </div>
      {pickerOpen && (
        <StyledPresets className="presets-list" style={{ zIndex: 4999 }}>
          {PRESETS.map((preset: any) => (
            <div
              className="preset-item"
              key={preset.id}
              onClick={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
                onChange({ start: preset.start, end: preset.end });
              }}
            >
              {preset.start} - {preset.end}
            </div>
          ))}
        </StyledPresets>
      )}
    </div>
  );
};

const StyledPresets = styled.div`
  background: #fff;
  padding: 10px;
  box-shadow: 0px 2px 16px rgba(18, 52, 77, 0.16);
  border-radius: 4px;
  margin-top: 5px;
  position: absolute;
  width: 280px;
  right: 10px;

  .preset-item {
    font-weight: 500;
    font-size: 13px;
    line-height: 20px;
    color: #12344d;
    border-radius: 4px;
    padding: 6px 10px;
    cursor: pointer;
    &:hover {
      background: #e5f2fd;
    }
  }
`;

const ScaleStartField = ({
  defaultValue,
  questionIndex,
  control,
  question,
  disabled,
}: {
  defaultValue: any;
  questionIndex: any;
  control: any;
  question: any;
  disabled: boolean;
}) => {
  const { field } = useController({
    name: `question_details.questions[${questionIndex}].type_info.validation.min`,
    control,
    defaultValue,
  });

  const { field: questionField } = useController({
    name: `question_details.questions[${questionIndex}]`,
    control,
    defaultValue: question,
  });

  const options = [
    { id: 0, label: 0 },
    { id: 1, label: 1 },
  ];

  return (
    <StyledButtonMenu>
      <div className="button-menu extended">
        {options.map((option: any) => (
          <div
            key={option.id}
            className={`${
              option.id === questionField.value.type_info.validation.min
                ? 'active'
                : ''
            } ${option.id} button-value`}
            onClick={(e: any) => {
              e.stopPropagation();
              const nextQuestion = {
                ...questionField.value,
                type_info: {
                  ...questionField.value.type_info,
                  validation: {
                    ...questionField.value.type_info.validation,
                    min: option.id,
                  },
                  choices:
                    option.id === 1
                      ? questionField.value.type_info.choices.filter(
                          (c: any) => c.value !== '0',
                        )
                      : [
                          {
                            value: '0',
                            position: 0,
                            dependent_question_names:
                              questionField.value.type_info.choices[0]
                                .dependent_question_names,
                          },
                          ...questionField.value.type_info.choices,
                        ],
                },
              };
              !disabled && questionField.onChange(nextQuestion);
            }}
          >
            <span className="label">
              &nbsp;&nbsp;{option.label}&nbsp;&nbsp;
            </span>
          </div>
        ))}
      </div>
    </StyledButtonMenu>
  );
};
